import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authApi } from '../../services/api';
import '../../components/landing/landing.css';
import toast from 'react-hot-toast';

export default function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    tenant_name: '',
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.password_confirmation) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const { data } = await authApi.register(form);

      if (data?.requires_verification) {
        toast.success('Workspace provisioned. Check your email for a 6-digit code.');
        navigate('/verify-otp', {
          state: { email: data.email || form.email, devOtp: data.dev_otp || '' },
        });
        return;
      }

      // Fallback: legacy direct-token response (shouldn't normally happen).
      if (data?.token) {
        localStorage.setItem('calibza_token', data.token);
        localStorage.setItem('calibza_user', JSON.stringify(data.user));
        window.location.href = '/app';
      }
    } catch (err) {
      const errs = err.response?.data?.errors;
      if (errs) Object.values(errs).flat().forEach(m => toast.error(m));
      else toast.error('Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="cz-root">
      <div className="cz-grain" aria-hidden="true" />

      <div className="cz-auth">
        {/* ── LEFT: onboarding certificate ───────────────────────── */}
        <aside className="cz-auth-side">
          <Link to="/" className="cz-wordmark">
            <span className="cz-wordmark-c">C</span>alibZa
            <sup className="cz-wordmark-tm">®</sup>
          </Link>

          <dl className="cz-auth-meta">
            <dt>Document</dt><dd>Auth-02 · Workspace provisioning</dd>
            <dt>Class</dt><dd>Free pilot · 20 operators · 90 days</dd>
            <dt>Issued</dt><dd>{today}</dd>
          </dl>

          <h1 className="cz-auth-headline">
            Commission your <em>instrument.</em>
          </h1>

          <p className="cz-auth-lede">
            A fresh tenant, calibrated to your org. Upload your roster, pick a
            review cycle, and start measuring in under ninety days.
          </p>

          <div className="cz-auth-stamp">
            <div className="cz-auth-stamp-head">
              <span>Pilot inclusions</span>
              <strong>All modules</strong>
            </div>
            <div className="cz-auth-stamp-rows">
              <span className="k">Reviews</span><span className="bar" /><span className="v">Unlimited cycles</span>
              <span className="k">Calibration</span><span className="bar" /><span className="v">Dynamic distribution</span>
              <span className="k">Analytics</span><span className="bar" /><span className="v">Anti-gaming · 360°</span>
              <span className="k">Integrations</span><span className="bar" /><span className="v">HRIS · Slack · SSO</span>
              <span className="k">Support</span><span className="bar" /><span className="v">White-glove onboarding</span>
            </div>
          </div>
        </aside>

        {/* ── RIGHT: form ─────────────────────────────────────────── */}
        <main className="cz-auth-panel">
          <div className="cz-auth-record">
            <span>Serial № <strong>CZ-NEW-{today}</strong></span>
            <span>Rev. A · Pilot</span>
          </div>

          <div className="cz-auth-panel-inner">
            <header className="cz-auth-panel-head">
              <span className="cz-mono-label">§ 01 · New workspace</span>
              <h2>Open a <em>tenant.</em></h2>
              <p>Up to 20 employees · no credit card · dismantle anytime.</p>
            </header>

            <form className="cz-auth-form" onSubmit={handleSubmit} autoComplete="on">
              <div className="cz-field">
                <label htmlFor="tenant_name" className="cz-field-label">
                  <span className="cz-field-label-num">01</span>
                  <span>Company name</span>
                </label>
                <input
                  id="tenant_name" type="text" required autoFocus
                  autoComplete="organization"
                  className="cz-input" placeholder="Acme Corp"
                  value={form.tenant_name} onChange={set('tenant_name')}
                />
              </div>

              <div className="cz-field">
                <label htmlFor="name" className="cz-field-label">
                  <span className="cz-field-label-num">02</span>
                  <span>Your name</span>
                </label>
                <input
                  id="name" type="text" required
                  autoComplete="name"
                  className="cz-input" placeholder="Jane Smith"
                  value={form.name} onChange={set('name')}
                />
              </div>

              <div className="cz-field">
                <label htmlFor="email" className="cz-field-label">
                  <span className="cz-field-label-num">03</span>
                  <span>Work email</span>
                </label>
                <input
                  id="email" type="email" required
                  autoComplete="email"
                  className="cz-input" placeholder="jane@acme.com"
                  value={form.email} onChange={set('email')}
                />
              </div>

              <div className="cz-field-row">
                <div className="cz-field">
                  <label htmlFor="password" className="cz-field-label">
                    <span className="cz-field-label-num">04</span>
                    <span>Password</span>
                  </label>
                  <input
                    id="password" type="password" required minLength={8}
                    autoComplete="new-password"
                    className="cz-input" placeholder="Min. 8 chars"
                    value={form.password} onChange={set('password')}
                  />
                </div>

                <div className="cz-field">
                  <label htmlFor="password_confirmation" className="cz-field-label">
                    <span className="cz-field-label-num">05</span>
                    <span>Confirm</span>
                  </label>
                  <input
                    id="password_confirmation" type="password" required
                    autoComplete="new-password"
                    className="cz-input" placeholder="Repeat password"
                    value={form.password_confirmation} onChange={set('password_confirmation')}
                  />
                </div>
              </div>

              <button type="submit" disabled={loading} className="cz-auth-submit">
                {loading ? 'Provisioning workspace…' : 'Open workspace'}
                <span className="cz-btn-arrow">→</span>
              </button>
            </form>

            <div className="cz-auth-alt">
              <span>Already provisioned?</span>
              <Link to="/login">Sign in instead →</Link>
            </div>

            <p className="cz-auth-legal">
              By opening a workspace you agree to our <Link to="/terms">Terms</Link> and <Link to="/privacy">Privacy Policy</Link>. Workspace data is encrypted at rest and in transit.
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
